import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Html5QrcodeScanner } from 'html5-qrcode';
import axiosInstance from '../../../../../api';

const TransferCaseModal = ({ show, onHide }) => {
  const title = 'Add Transfer Case';
  const initialFormData = {
    name: '',
    diagnose: '',
    bracelet_id: '',
    doctor: '',
    doctor_name: '',
    age: '',
    gender: '',
    clinic_no: '',
    request_details: '',
    escort_team: {
      escort_physician: '',
      escort_nurse: '',
      escort_paramedic: '',
      other: ''
    },
    documents: {},
  };
  const [medicalCenters, setMedicalCenters] = useState([]);
  const [transferFrom, setTransferFrom] = useState('');
  const [transferTo, setTransferTo] = useState('');
  const [filteredTransferTo, setFilteredTransferTo] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [fileInputs, setFileInputs] = useState([0]);
  const fileInputRefs = useRef([]);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [medicalCenterId, setMedicalCenterId] = useState(null);
  const MAX_FILES = 3;

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      setFormData(initialFormData);
      setErrors({});
      setFileInputs([0]);
      setTransferFrom('');
      setTransferTo('');
      setDoctors([]);
      setShowScanner(false);
    }
  }, [show]);

  /** ---------------- QR Scanner ---------------- */
  useEffect(() => {
    let scanner;
    if (showScanner && window.innerWidth < 992) {
      scanner = new Html5QrcodeScanner("reader", { fps: 10, qrbox: 250 });
      scanner.render((decodedText) => {
        setFormData(prev => ({ ...prev, bracelet_id: decodedText }));
        setErrors(prev => ({ ...prev, bracelet_id: '' }));
        setShowScanner(false);
        scanner.clear();
      });
    }
    return () => {
      if (scanner) scanner.clear();
    };
  }, [showScanner]);

  /** ---------------- File Handling ---------------- */
  const handleFileChange = (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jfif', 'application/pdf'];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, [`documents${index + 1}`]: 'Only PDF, JPG, PNG, WEBP allowed' }));
      return;
    }
    if (file.size > maxSize) {
      setErrors(prev => ({ ...prev, [`documents${index + 1}`]: 'File must be under 5MB' }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [`documents${index + 1}`]: file,
      },
    }));
    setErrors(prev => ({ ...prev, [`documents${index + 1}`]: '', documents: '' }));
  };
  const addFileInput = () => {
    if (fileInputs.length < MAX_FILES) {
      setFileInputs([...fileInputs, fileInputs.length]);
    }
  };
  const removeFileInput = (index) => {
    const newFileInputs = fileInputs.filter((_, i) => i !== index);
    setFileInputs(newFileInputs.length ? newFileInputs.map((_, i) => i) : [0]);
    const newDocs = { ...formData.documents };
    delete newDocs[`documents${index + 1}`];
    const updatedDocs = {};
    let counter = 1;
    Object.values(newDocs).forEach(file => {
      if (file) {
        updatedDocs[`documents${counter}`] = file;
        counter++;
      }
    });
    setFormData(prev => ({ ...prev, documents: updatedDocs }));
    setErrors(prev => ({ ...prev, [`documents${index + 1}`]: '', documents: '' }));
  };
  const getDisplayFileName = (file) => {
    if (!file) return '';
    const maxLength = 20;
    const name = file.name;
    return name.length > maxLength
      ? `${name.substring(0, maxLength - 3)}...${name.split('.').pop()}`
      : name;
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
  };

  /** ---------------- Fetch Medical Centers & Doctors ---------------- */
  useEffect(() => {
    const role = sessionStorage.getItem('Role_Name');
    const medCenterId = sessionStorage.getItem('Medical_Center_Id') || sessionStorage.getItem('medical_center_id');
    setUserRole(role);
    setMedicalCenterId(medCenterId);
    const fetchMedicalCenters = async () => {
      try {
        const response = await axiosInstance.get('/ems/medical-centers');
        const centers = response.data || [];
        setMedicalCenters(centers);
        if ((role === 'Hospital Representative (Nurse)' || role === 'EMS Resource Manager') && medCenterId && medCenterId !== '0') {
          const userCenter = centers.find(c => c.id === parseInt(medCenterId));
          if (userCenter) {
            setTransferFrom(String(userCenter.id));
            setFilteredTransferTo(centers.filter(c => c.id !== userCenter.id));
          } else {
            setFilteredTransferTo(centers);
          }
        } else {
          setFilteredTransferTo(centers);
        }
      } catch (err) {
        console.error('Error fetching medical centers:', err);
      }
    };
    if (show) fetchMedicalCenters();
  }, [show]);
  useEffect(() => {
    if (transferFrom) {
      setFilteredTransferTo(medicalCenters.filter(c => c.id !== parseInt(transferFrom)));
    } else {
      setFilteredTransferTo(medicalCenters);
    }
  }, [transferFrom, medicalCenters]);
  useEffect(() => {
    if (transferFrom) {
      const fetchDoctors = async () => {
        try {
          const res = await axiosInstance.get(`/ems/get-specefic-medical-center-doctors-list/${transferFrom}`);
          setDoctors((res.data || []).map(d => ({
            id: d.id,
            name: `${d.first_name || ''} ${d.last_name || ''}`.trim() || 'Unnamed Doctor'
          })));
        } catch {
          setDoctors([]);
        }
      };
      fetchDoctors();
    } else {
      setDoctors([]);
    }
  }, [transferFrom]);

  /** ---------------- Input Handlers ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      // Only allow letters, spaces, dots, commas, hyphens
      if (/^[a-zA-Z\s.,-]*$/.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, name: '' }));
      } else {
        setErrors(prev => ({ ...prev, name: 'Case Name can only contain letters, spaces, and basic punctuation (., -)' }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleEscortTeamChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      escort_team: { ...prev.escort_team, [name]: value }
    }));
  };

  const handleDoctorChange = (e) => {
    const value = e.target.value;
    const selected = doctors.find(d => d.id === parseInt(value));
    setFormData(prev => ({
      ...prev,
      doctor: value,
      doctor_name: selected ? selected.name : ''
    }));
    setErrors(prev => ({ ...prev, doctor: '' }));
  };

  /** ---------------- Validation ---------------- */
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    if (!formData.name.trim()) {
      newErrors.name = 'Case Name is required';
      isValid = false;
    } else if (!/^[a-zA-Z\s.,-]+$/.test(formData.name.trim())) {
      newErrors.name = 'Case Name can only contain letters, spaces, and basic punctuation (., -)';
      isValid = false;
    }
    if (!transferFrom) {
      newErrors.transferFrom = 'Transfer From is required';
      isValid = false;
    }
    if (!transferTo) {
      newErrors.transferTo = 'Transfer To is required';
      isValid = false;
    }
    if (!formData.diagnose.trim()) {
      newErrors.diagnose = 'Diagnose is required';
      isValid = false;
    }
    if (!formData.doctor) {
      newErrors.doctor = 'Please select a doctor';
      isValid = false;
    }
    if (!formData.bracelet_id.trim()) {
      newErrors.bracelet_id = 'Bracelet ID is required';
      isValid = false;
    }
    if (!formData.clinic_no.trim()) {
      newErrors.clinic_no = 'Clinic No is required';
      isValid = false;
    }
    if (!formData.request_details.trim()) {
      newErrors.request_details = 'Request details are required';
      isValid = false;
    }
    if (Object.keys(formData.documents).length === 0) {
      newErrors.documents = 'At least one document is required';
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };

  /** ---------------- Submit ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const documentsBase64 = {};
      for (const [key, file] of Object.entries(formData.documents)) {
        if (file instanceof File) {
          documentsBase64[key] = await fileToBase64(file);
        }
      }

      const userId = sessionStorage.getItem('user_id');
      const userName = sessionStorage.getItem('User_Name');
      const originalCaseId = sessionStorage.getItem('currentCaseId');
      const nurse = ['EMS Requester', 'EMS Resource Manager', 'Admin'].includes(userRole) ? null : userId;
      const nurse_name = ['EMS Requester', 'EMS Resource Manager', 'Admin'].includes(userRole) ? null : userName;
      const payload = {
        user_id: Number(userId) || null,
        case_name: formData.name.trim(),
        age: formData.age ? parseInt(formData.age) : null,
        gender: formData.gender || null,
        nearest_medical_center: parseInt(transferTo),
        transfer_from_medical_center: parseInt(transferFrom),
        diagnose: formData.diagnose.trim(),
        triage_card: "Red",
        bracelet_id: formData.bracelet_id.trim(),
        doctor: parseInt(formData.doctor),
        doctor_name: formData.doctor_name,
        original_case_id: originalCaseId ? parseInt(originalCaseId) : null,
        case_status: 2,
        nurse: nurse ? Number(nurse) : null,
        nurse_name: nurse_name || null,
        doctor_approval_status: 0,
        clinic_no: formData.clinic_no.trim(),
        request_details: formData.request_details.trim(),
        escort_team: formData.escort_team,
        ...documentsBase64
      };

      await axiosInstance.post("/ems/original-case-transfer", payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("auth_token") || sessionStorage.getItem("authToken")}`
        }
      });
      alert('Transfer case added successfully!');
      onHide(true);
    } catch (error) {
      console.error('Error:', error);
      alert(error.response?.data?.message || 'Failed to save transfer case');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={() => onHide(false)} size="lg" centered scrollable>
      <Modal.Header className="d-flex align-items-center">
        <Modal.Title>{title}</Modal.Title>
        <button type="button" className="btn-close" onClick={() => onHide(false)} />
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label>Case Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                />
                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </div>
            </div>

            <div className="col-md-6">
              <div className="mb-3">
                <label>Age</label>
                <input
                  type="number"
                  className="form-control"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label>Gender</label>
                <select className="form-control form-select" name="gender" value={formData.gender} onChange={handleChange}>
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
            </div>

            <div className="col-md-6">
              <div className="mb-3">
                <label>Transfer From <span className="text-danger">*</span></label>
                <select
                  className={`form-control form-select ${errors.transferFrom ? 'is-invalid' : ''}`}
                  value={transferFrom}
                  onChange={(e) => {
                    setTransferFrom(e.target.value);
                    setFormData(prev => ({ ...prev, doctor: '', doctor_name: '' }));
                    setDoctors([]);
                    setErrors(prev => ({ ...prev, transferFrom: '' }));
                  }}
                  disabled={['Hospital Representative (Nurse)', 'EMS Resource Manager'].includes(userRole) && medicalCenterId && medicalCenterId !== '0'}
                >
                  <option value="">Select Medical Center</option>
                  {medicalCenters.map(center => (
                    <option key={center.id} value={center.id}>{center.name}</option>
                  ))}
                </select>
                {errors.transferFrom && <div className="invalid-feedback">{errors.transferFrom}</div>}
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label>Transfer To <span className="text-danger">*</span></label>
                <select
                  className={`form-control form-select ${errors.transferTo ? 'is-invalid' : ''}`}
                  value={transferTo}
                  onChange={(e) => {
                    setTransferTo(e.target.value);
                    setErrors(prev => ({ ...prev, transferTo: '' }));
                  }}
                >
                  <option value="">Select Medical Center</option>
                  {filteredTransferTo.map(center => (
                    <option key={center.id} value={center.id}>{center.name}</option>
                  ))}
                </select>
                {errors.transferTo && <div className="invalid-feedback">{errors.transferTo}</div>}
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label>Diagnose<span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.diagnose ? 'is-invalid' : ''}`}
                  name="diagnose"
                  value={formData.diagnose}
                  onChange={handleChange}
                />
                {errors.diagnose && <div className="invalid-feedback">{errors.diagnose}</div>}
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label>Doctor <span className="text-danger">*</span></label>
                <select
                  className={`form-control form-select ${errors.doctor ? 'is-invalid' : ''}`}
                  value={formData.doctor}
                  onChange={handleDoctorChange}
                >
                  <option value="">Select Doctor</option>
                  {doctors.map(doctor => (
                    <option key={doctor.id} value={doctor.id}>{doctor.name}</option>
                  ))}
                </select>
                {errors.doctor && <div className="invalid-feedback">{errors.doctor}</div>}
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label>Bracelet ID <span className="text-danger">*</span></label>
                <div className="d-flex align-items-center">
                  <input
                    type="text"
                    className={`form-control ${errors.bracelet_id ? 'is-invalid' : ''}`}
                    name="bracelet_id"
                    value={formData.bracelet_id}
                    onChange={handleChange}
                  />
                  <i
                    className="ti ti-scan text-primary fs-4 ms-3 d-lg-none d-block"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setShowScanner(true)}
                  />
                </div>
                {errors.bracelet_id && <div className="invalid-feedback">{errors.bracelet_id}</div>}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12 mb-3">
              <label>Clinic No <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-control ${errors.clinic_no ? 'is-invalid' : ''}`}
                name="clinic_no"
                value={formData.clinic_no}
                onChange={handleChange}
              />
              {errors.clinic_no && <div className="invalid-feedback">{errors.clinic_no}</div>}
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <label>Request Details <span className="text-danger">*</span></label>
              <textarea
                className={`form-control ${errors.request_details ? 'is-invalid' : ''}`}
                name="request_details"
                value={formData.request_details}
                onChange={handleChange}
                rows="3"
                placeholder="Describe the transfer request..."
              />
              {errors.request_details && <div className="invalid-feedback">{errors.request_details}</div>}
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <label>Escort Team (Optional)</label>
              <div className="row g-2">
                <div className="col-md-6">
                  <input type="text" className="form-control" name="escort_physician" value={formData.escort_team.escort_physician} onChange={handleEscortTeamChange} placeholder="Escort Physician" />
                </div>
                <div className="col-md-6">
                  <input type="text" className="form-control" name="escort_nurse" value={formData.escort_team.escort_nurse} onChange={handleEscortTeamChange} placeholder="Escort Nurse" />
                </div>
                <div className="col-md-6">
                  <input type="text" className="form-control" name="escort_paramedic" value={formData.escort_team.escort_paramedic} onChange={handleEscortTeamChange} placeholder="Escort Paramedic" />
                </div>
                <div className="col-md-6">
                  <input type="text" className="form-control" name="other" value={formData.escort_team.other} onChange={handleEscortTeamChange} placeholder="Other" />
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12 mb-3">
              <label>
                Documents Upload <span className="text-danger">*</span>
              </label>
              <small className="text-muted d-block mb-2">
                Allowed file types: PDF, JPG, PNG, WEBP, JFIF. Maximum 3 files.
              </small>
              {errors.documents && <div className="text-danger mb-2">{errors.documents}</div>}
              {fileInputs.map((_, index) => (
                <div key={index} className="input-group mb-2">
                  <input
                    type="file"
                    className={`form-control ${errors[`documents${index + 1}`] ? 'is-invalid' : ''}`}
                    onChange={e => handleFileChange(e, index)}
                    accept="image/jpeg,image/png,image/webp,image/jfif,application/pdf"
                  />
                  {formData.documents[`documents${index + 1}`] && (
                    <OverlayTrigger placement="top" overlay={<Tooltip>{formData.documents[`documents${index + 1}`].name}</Tooltip>}>
                      <span className="input-group-text" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {getDisplayFileName(formData.documents[`documents${index + 1}`])}
                      </span>
                    </OverlayTrigger>
                  )}
                  <div className="input-group-append d-flex align-items-center">
                    {index === fileInputs.length - 1 && fileInputs.length < MAX_FILES && (
                      <button type="button" className="btn btn-sm" onClick={addFileInput}>
                        <i className="ti ti-circle-plus text-success"></i>
                      </button>
                    )}
                    {fileInputs.length > 1 && (
                      <button type="button" className="btn btn-sm" onClick={() => removeFileInput(index)}>
                        <i className="ti ti-trash text-danger"></i>
                      </button>
                    )}
                  </div>
                  {errors[`documents${index + 1}`] && <div className="invalid-feedback d-block">{errors[`documents${index + 1}`]}</div>}
                </div>
              ))}
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-danger" onClick={() => onHide(false)} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-1"></span> Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
      </Modal.Footer>
      {/* QR Scanner Overlay */}
      {showScanner && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1055 }}>
          <div className="d-flex justify-content-end p-3">
            <i className="ti ti-x text-white fs-2" style={{ cursor: 'pointer' }} onClick={() => setShowScanner(false)}></i>
          </div>
          <div className="d-flex justify-content-center align-items-center" style={{ height: '90%' }}>
            <div id="reader" className="border rounded" style={{ width: '100%', maxWidth: '400px' }}></div>
          </div>
        </div>
      )}
    </Modal>
  );
};
export default TransferCaseModal;